// GE 국제 체크아웃 실패를 **서버 로그로** 남긴다 — #5807.
//
// 결제창은 GE iframe 안에서 돌아서, 고객이 결제를 못 하고 나가도 우리 쪽엔 아무 기록이 없었다(브라우저 콘솔은
// 고객 화면에만 찍혀 우리가 볼 수 없다). 그래서 브라우저가 실패를 짧은 코드로 `/api/global-e/client-event/` 에
// 보내고, 서버가 `[GE_CLIENT_EVENT]` 한 줄로 Vercel 로그에 남긴다.
//
// | kind               | 언제                                                             | 출처                         |
// | ------------------ | ---------------------------------------------------------------- | ---------------------------- |
// | `load_fail`        | 결제창이 안 떴다 — 우리 실패 안내를 띄운 순간(사유: `cart-token 422` 등) | `checkout-load-watch.ts`     |
// | `load_recovered`   | 안내를 띄운 뒤 늦게라도 결제창이 떴다(오탐 확인용)                 | `checkout-load-watch.ts`     |
// | `checkout_failure` | GE `OnClientEvent` 의 `CHECKOUT_FAILURE`(`failureCode`·`reasonCode`) | GE 문서 Advanced Checkout Events |
// | `server_error`     | GE `OnClientEvent` 의 `ON_SERVER_ERROR`                           | 같음                         |
//
// ⚠️ GE 두 이벤트는 **실제로 오는지 미실측**이다(2026-10-27 — 구독 등록은 됐지만 dev 에서 실패를 재현할 수단이
// 없었다. DG 제한이 풀려 「cannot be shipped」도 안 뜬다). 온 적이 있으면 로그에 남는다 — 안 오면 그만이다.
// `load_fail` 은 우리 코드라 확실히 남는다(dev 에서 TR 로 재현 가능).
//
// 금액·PII 는 싣지 않는다. 국가는 서버가 `bh_ccode` 쿠키에서 읽는다(클라가 보내지 않는다).

export const GE_CLIENT_EVENT_KINDS = [
  "load_fail",
  "load_recovered",
  "checkout_failure",
  "server_error",
] as const;

export type GeClientEventKind = (typeof GE_CLIENT_EVENT_KINDS)[number];

export interface GeClientEvent {
  kind: GeClientEventKind;
  /** load_* 의 사유(`cart-token 422` · `no checkout step in 20000ms`) */
  reason?: string;
  /** GE `CHECKOUT_FAILURE` 의 `failureCode`(`RestrictedProducts` 등) */
  failureCode?: string;
  /** GE 의 `reasonCode` — 없을 수 있다 */
  reasonCode?: string;
}

export const GE_CLIENT_EVENT_PATH = "/api/global-e/client-event/";

/** 로그 한 줄에 싣는 문자열 값 — 짧은 코드만. 그 밖(긴 문자열·제어문자 등)은 버린다. */
const SAFE_VALUE = /^[\w .:/()-]{1,80}$/;

export function safeEventValue(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value))
    value = String(value);
  if (typeof value !== "string") return undefined;
  const v = value.trim();
  return SAFE_VALUE.test(v) ? v : undefined;
}

export function isGeClientEventKind(
  value: unknown,
): value is GeClientEventKind {
  return (
    typeof value === "string" &&
    (GE_CLIENT_EVENT_KINDS as readonly string[]).includes(value)
  );
}

/**
 * 브라우저 → 서버 보고. `sendBeacon` 이 우선이다(페이지를 떠나는 중에도 간다). 실패는 삼킨다 — 관측 코드가
 * 결제 화면을 깨면 안 된다.
 */
export function reportGeClientEvent(event: GeClientEvent): void {
  if (typeof window === "undefined") return;
  try {
    const body = JSON.stringify(event);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        GE_CLIENT_EVENT_PATH,
        new Blob([body], { type: "application/json" }),
      );
      if (sent) return;
    }
    void fetch(GE_CLIENT_EVENT_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // 무시 — 위 주석
  }
}

/** GE `OnClientEvent` 에서 보고할 이벤트 이름 → kind */
const REPORTED_GE_EVENTS: Record<string, GeClientEventKind> = {
  CHECKOUT_FAILURE: "checkout_failure",
  ON_SERVER_ERROR: "server_error",
};

/**
 * GE `OnClientEvent(source, data)` 를 보고 대상으로 바꾼다. 대상이 아니면 `null`.
 *
 * `source` 는 문서상 `GEMerchantUtils.ClientEvents.CHECKOUT_FAILURE` 상수다. 그 값이 이름 문자열인지 다른 값인지
 * 실측하지 못해, 이름과 상수 양쪽으로 맞춘다(상수 객체가 페이지에 있으면 그 값도 본다).
 */
export function toGeClientEvent(
  source: unknown,
  data: unknown,
  constants?: Record<string, unknown> | null,
): GeClientEvent | null {
  const name =
    Object.keys(REPORTED_GE_EVENTS).find(
      (k) =>
        source === k ||
        // 상수 객체에 키가 없을 때 `undefined === undefined` 로 맞지 않게
        (constants?.[k] !== undefined && source === constants[k]),
    ) ?? null;
  if (!name) return null;
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    kind: REPORTED_GE_EVENTS[name],
    failureCode: safeEventValue(d.failureCode),
    reasonCode: safeEventValue(d.reasonCode),
  };
}
